/**
 * Encomendas — MORADOR (Portal), 0.1.51. Só consulta.
 *
 * O morador vê as encomendas da PRÓPRIA unidade (aguardando com o código de retirada
 * em destaque + histórico) e gerencia a lista de pessoas AUTORIZADAS a retirar por ele.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, Loader2, Package, PackageCheck, Trash2, UserPlus } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { fmtDataHora as fmt } from "../lib/datas";

interface Encomenda {
  id: number; unidade: string; tipo: string | null; remetente: string | null;
  transportadora: string | null; status: string; codigo_retirada: string | null;
  recebido_em: string | null; retirado_por_nome: string | null; retirado_em: string | null;
  retirado_por_terceiro: boolean;
}
interface Resp { condominio_id: number; itens: Encomenda[]; pode_gerir?: boolean; unidades?: string[]; unidades_morador?: string[] }
interface Autorizado { id: number; unidade_norm: string; nome: string; documento: string | null; observacao: string | null }


export function EncomendasMoradorPage({ condominioId, onVoltar }: { condominioId: number; onVoltar: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<Resp>({
    queryKey: ["encomendas-morador", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/encomendas`),
  });
  const { data: autz } = useQuery<Autorizado[]>({
    queryKey: ["autorizados", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/autorizados`),
  });

  const [novo, setNovo] = useState(false);
  const [nome, setNome] = useState("");
  const [doc, setDoc] = useState("");
  const [obs, setObs] = useState("");
  const [unidadeSel, setUnidadeSel] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  // Se o usuário TEM apartamento próprio (por e-mail) — inclusive um síndico que também é
  // morador — usa a própria unidade automaticamente (sem seletor). Só quando NÃO tem
  // unidade vinculada (ex.: Dono puro) e é gestor é que mostramos o seletor de apartamento.
  const minhasUnidades = data?.unidades_morador ?? [];
  const precisaEscolherUnidade = !!data?.pode_gerir && minhasUnidades.length === 0;

  const addAut = useMutation({
    mutationFn: () => api.post(`/condominios/${condominioId}/autorizados`, {
      nome: nome.trim(), documento: doc.trim() || null, observacao: obs.trim() || null,
      unidade: precisaEscolherUnidade ? unidadeSel : undefined,
    }),
    onSuccess: () => {
      setNovo(false); setNome(""); setDoc(""); setObs(""); setUnidadeSel(""); setErro(null);
      qc.invalidateQueries({ queryKey: ["autorizados", condominioId] });
    },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível salvar."),
  });
  const delAut = useMutation({
    mutationFn: (id: number) => api.delete(`/autorizados/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["autorizados", condominioId] }),
  });

  const itens = data?.itens ?? [];
  const aguardando = itens.filter((e) => e.status === "aguardando");
  const historico = itens.filter((e) => e.status !== "aguardando");

  return (
    <div className="max-w-lg mx-auto px-1">
      <div className="flex items-center justify-between pt-2 pb-4">
        <button onClick={onVoltar}
          className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-md border text-slate-600 bg-white border-slate-200 hover:bg-slate-50">
          <ArrowLeft size={16} /> Voltar
        </button>
        <div className="flex items-center gap-2 text-slate-700 font-semibold"><Package size={18} /> Minhas Encomendas</div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 text-slate-500 py-10"><Loader2 size={18} className="animate-spin" /> Carregando…</div>
      ) : (
        <>
          {aguardando.length === 0 ? (
            <div className="text-center text-slate-400 py-8 text-sm">Nenhuma encomenda aguardando retirada.</div>
          ) : (
            <div className="space-y-2.5">
              {aguardando.map((e) => (
                <div key={e.id} className="p-4 rounded-2xl border border-cyan-200 bg-cyan-50">
                  <div className="font-semibold text-slate-800 flex items-center gap-2"><Package size={16} className="text-cyan-700" /> {e.tipo || "Encomenda"} na portaria</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Chegou em {fmt(e.recebido_em)}
                    {e.remetente && <span> · de {e.remetente}</span>}
                    {e.transportadora && <span> · {e.transportadora}</span>}
                  </div>
                  {e.codigo_retirada && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs text-slate-500">Código de retirada:</span>
                      <span className="text-xl font-mono font-bold tracking-widest text-cyan-800 bg-white px-3 py-1 rounded-lg border border-cyan-200">{e.codigo_retirada}</span>
                    </div>
                  )}
                  <p className="text-[11px] text-slate-500 mt-2">Mostre este código na portaria para retirar. Você pode autorizar outra pessoa (lista abaixo).</p>
                </div>
              ))}
            </div>
          )}

          {/* Autorizados a retirar */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-slate-700">Pessoas autorizadas a retirar por você</h2>
              {!novo && (
                <button onClick={() => { setNovo(true); setErro(null); }}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-cyan-700 text-white"><UserPlus size={14} /> Autorizar</button>
              )}
            </div>
            {novo && (
              <div className="p-3 rounded-xl border border-slate-200 bg-white space-y-2 mb-2">
                {precisaEscolherUnidade && (
                  <label className="text-xs text-slate-500 flex flex-col gap-1">
                    Apartamento (você é gestor — escolha a unidade)
                    <select value={unidadeSel} onChange={(e) => setUnidadeSel(e.target.value)}
                      className="text-sm rounded-md border border-slate-300 px-2 py-2 bg-white">
                      <option value="">Escolha o apartamento…</option>
                      {(data?.unidades ?? []).map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </label>
                )}
                <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome da pessoa"
                  className="w-full text-sm rounded-md border border-slate-300 px-3 py-2" />
                <div className="grid grid-cols-2 gap-2">
                  <input value={doc} onChange={(e) => setDoc(e.target.value)} placeholder="Documento (opcional)"
                    className="text-sm rounded-md border border-slate-300 px-3 py-2" />
                  <input value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Ex.: diarista, AP 302"
                    className="text-sm rounded-md border border-slate-300 px-3 py-2" />
                </div>
                {erro && <div className="text-sm text-rose-700">{erro}</div>}
                <div className="flex gap-2">
                  <button onClick={() => addAut.mutate()} disabled={addAut.isPending || !nome.trim() || (precisaEscolherUnidade && !unidadeSel)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-cyan-700 text-white text-sm disabled:opacity-50">
                    {addAut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Salvar
                  </button>
                  <button onClick={() => { setNovo(false); setErro(null); }} className="px-3 py-1.5 rounded-md border border-slate-300 text-slate-600 text-sm">Cancelar</button>
                </div>
              </div>
            )}
            {(autz ?? []).length === 0 ? (
              <p className="text-xs text-slate-400">Ninguém autorizado ainda. Só você retira (com o código).</p>
            ) : (
              <div className="space-y-1.5">
                {(autz ?? []).map((a) => (
                  <div key={a.id} className="flex items-center justify-between p-2.5 rounded-lg border border-slate-200 bg-white">
                    <div className="text-sm text-slate-700">{a.nome}{a.observacao ? <span className="text-slate-400"> · {a.observacao}</span> : null}{a.documento ? <span className="text-slate-400"> · {a.documento}</span> : null}</div>
                    <button onClick={() => { if (confirm(`Remover ${a.nome} da lista?`)) delAut.mutate(a.id); }} className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Histórico */}
          {historico.length > 0 && (
            <div className="mt-6">
              <h2 className="text-sm font-semibold text-slate-700 mb-2">Histórico</h2>
              <div className="space-y-1.5">
                {historico.map((e) => (
                  <div key={e.id} className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-200 bg-white text-sm text-slate-600">
                    <PackageCheck size={15} className="text-emerald-600 flex-shrink-0" />
                    <span>{e.tipo || "Encomenda"} — {e.status === "retirada" ? `retirada em ${fmt(e.retirado_em)}${e.retirado_por_terceiro ? " (autorizado)" : ""}` : "devolvida"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
